import { z } from "zod";
import { MediumText, ShortText } from "./common.js";
export const PROCESS_TRACE_STATUSES = [
    "unknown",
    "observed",
    "supported",
    "needs_validation",
    "validated",
];
export const ProcessTraceStatusSchema = z.enum(PROCESS_TRACE_STATUSES);
const ProcessTraceStatusField = ProcessTraceStatusSchema.describe("Evidence maturity for this exact trace point. Use validated only after direct observation or a discriminating test; reaching POC alone is not validation.");
export const ProcessTraceRecognitionSchema = z
    .object({
    observation: MediumText.describe("What was first noticed at the Point of Recognition (POR). Leave blank when not established."),
    location: ShortText.describe("Where the problem was first detected or recognized."),
    evidence: MediumText.describe("Human-readable evidence supporting the recognition point, without transient tool or search identifiers."),
    status: ProcessTraceStatusField,
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.status === "validated" && value.evidence.trim() === "") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["evidence"],
            message: "validated recognition requires supporting evidence",
        });
    }
});
export const ProcessTraceOccurrenceSchema = z
    .object({
    location: ShortText.describe("Where expected and actual behavior first diverged at the Point of Occurrence (POO)."),
    expected: MediumText.describe("The standard or expected behavior at the occurrence point."),
    actual: MediumText.describe("The observed actual behavior at the occurrence point."),
    divergence: MediumText.describe("A concise should-versus-is statement describing the first supported divergence."),
    evidence: MediumText.describe("Human-readable evidence supporting the occurrence point, without transient tool or search identifiers."),
    status: ProcessTraceStatusField,
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.status === "validated" && value.evidence.trim() === "") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["evidence"],
            message: "validated occurrence requires supporting evidence",
        });
    }
});
export const ProcessTraceCauseSchema = z
    .object({
    cause: MediumText.describe("The observable process condition at the Point of Cause (POC), not an invented or untested explanation."),
    location: ShortText.describe("Where the causal process condition can be observed."),
    validationMethod: MediumText.describe("Direct observation or discriminating test that would validate or falsify this cause."),
    evidence: MediumText.describe("Human-readable evidence currently supporting the cause point, without transient tool or search identifiers."),
    status: ProcessTraceStatusField,
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.status !== "validated")
        return;
    if (value.evidence.trim() === "") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["evidence"],
            message: "validated cause requires supporting evidence",
        });
    }
    if (value.validationMethod.trim() === "") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["validationMethod"],
            message: "validated cause requires a validation method",
        });
    }
});
export const ProcessTraceParamsSchema = z
    .object({
    subject: ShortText.describe("Optional concise name for the problem or causal path being traced."),
    por: ProcessTraceRecognitionSchema.describe("Point of Recognition: where the problem was first noticed."),
    poo: ProcessTraceOccurrenceSchema.describe("Point of Occurrence: where expected and actual behavior first diverged."),
    poc: ProcessTraceCauseSchema.describe("Point of Cause: where the causal process condition can be observed."),
    conclusion: MediumText.describe("Concise trace conclusion that preserves uncertainty and does not claim validation without evidence."),
})
    .strict();
export const defaultProcessTraceParams = {
    subject: "",
    por: {
        observation: "",
        location: "",
        evidence: "",
        status: "unknown",
    },
    poo: {
        location: "",
        expected: "",
        actual: "",
        divergence: "",
        evidence: "",
        status: "unknown",
    },
    poc: {
        cause: "",
        location: "",
        validationMethod: "",
        evidence: "",
        status: "unknown",
    },
    conclusion: "",
};
//# sourceMappingURL=processTrace.js.map